import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';
import {
  TrendingUp,
  Shield,
  Info,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Target,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface IncomeSource {
  id: string;
  name: string;
  monthlyAmount: number;
  startAge: number;
  endAge?: number; // undefined = lifetime
  inflationAdjusted: boolean;
  type: 'social-security' | 'pension' | 'annuity' | 'other';
}

interface RetirementIncomeProjectionProps {
  currentAge?: number;
  retirementAge?: number;
  lifeExpectancy?: number;
  portfolioValue?: number;
  incomeSources?: IncomeSource[];
  monthlyExpenses?: number;
  expectedReturn?: number;
  volatility?: number;
  inflationRate?: number;
}

interface ProjectionDataPoint {
  age: number;
  socialSecurity: number;
  pension: number;
  portfolioIncome: number;
  annuity: number;
  totalIncome: number;
  expenses: number;
  portfolioBalance: number;
  // Scenario variations
  pessimisticTotal: number;
  optimisticTotal: number;
  pessimisticBalance: number;
  optimisticBalance: number;
  // With FIA layer
  fiaIncome: number;
  totalWithFia: number;
}

type MarketScenario = 'pessimistic' | 'average' | 'optimistic';

const SCENARIO_RETURNS: Record<MarketScenario, number> = {
  pessimistic: 0.04,
  average: 0.07,
  optimistic: 0.10,
};

export function RetirementIncomeProjection({
  currentAge: initialAge = 55,
  retirementAge: initialRetirement = 65,
  lifeExpectancy: initialLifeExpectancy = 95,
  portfolioValue: initialPortfolio = 800000,
  monthlyExpenses: initialExpenses = 6000,
  inflationRate: initialInflation = 0.025,
}: RetirementIncomeProjectionProps) {
  // Core inputs
  const [currentAge, setCurrentAge] = useState(initialAge);
  const [retirementAge, setRetirementAge] = useState(initialRetirement);
  const [lifeExpectancy, setLifeExpectancy] = useState(initialLifeExpectancy);
  const [portfolioValue, setPortfolioValue] = useState(initialPortfolio);
  const [monthlyExpenses, setMonthlyExpenses] = useState(initialExpenses);
  const [inflationRate, setInflationRate] = useState(initialInflation * 100);

  // Income sources state (simplified for demo, would come from LifetimeIncomePanel)
  const [socialSecurityAmount, setSocialSecurityAmount] = useState(2800);
  const [socialSecurityStartAge, setSocialSecurityStartAge] = useState(67);
  const [pensionAmount, setPensionAmount] = useState(1500);
  const [pensionStartAge, setPensionStartAge] = useState(65);
  const [hasPension, setHasPension] = useState(true);

  // Withdrawal strategy
  const [withdrawalRate, setWithdrawalRate] = useState(4.0);
  const [activeScenario, setActiveScenario] = useState<MarketScenario>('average');

  // Fixed Index Annuity modeling
  const [showFiaModeling, setShowFiaModeling] = useState(false);
  const [fiaLumpSum, setFiaLumpSum] = useState(200000);
  const [fiaPayoutRate, setFiaPayoutRate] = useState(5.5);
  const [fiaStartAge, setFiaStartAge] = useState(65);

  // UI state
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  // Calculate projections
  const projectionData = useMemo(() => {
    const data: ProjectionDataPoint[] = [];
    const yearsToProject = lifeExpectancy - currentAge;
    const inflation = inflationRate / 100;

    // Track portfolio balances for each scenario
    let portfolioBalanceAvg = portfolioValue;
    let portfolioBalancePessimistic = portfolioValue;
    let portfolioBalanceOptimistic = portfolioValue;

    for (let year = 0; year <= yearsToProject; year++) {
      const age = currentAge + year;
      const isRetired = age >= retirementAge;

      // Inflation-adjusted expenses
      const adjustedExpenses = monthlyExpenses * Math.pow(1 + inflation, year);

      // Social Security (inflation-adjusted after start)
      let socialSecurity = 0;
      if (age >= socialSecurityStartAge) {
        const yearsSinceSS = age - socialSecurityStartAge;
        socialSecurity = socialSecurityAmount * Math.pow(1 + inflation, yearsSinceSS);
      }

      // Pension
      let pension = 0;
      if (hasPension && age >= pensionStartAge) {
        // Pensions typically don't adjust for inflation (or have small COLA)
        pension = pensionAmount;
      }

      // Fixed Index Annuity income
      let fiaIncome = 0;
      if (showFiaModeling && age >= fiaStartAge) {
        // FIA provides guaranteed income based on payout rate
        fiaIncome = (fiaLumpSum * (fiaPayoutRate / 100)) / 12;
      }

      // Calculate guaranteed income total
      const guaranteedIncome = socialSecurity + pension;

      // Portfolio withdrawal calculations for each scenario
      const calculatePortfolioIncome = (
        balance: number,
        scenarioReturn: number
      ): { income: number; newBalance: number } => {
        if (!isRetired || balance <= 0) {
          // Pre-retirement: grow the portfolio
          const growth = balance * scenarioReturn;
          return { income: 0, newBalance: balance + growth };
        }

        // Calculate needed withdrawal (expenses minus guaranteed income)
        const incomeGap = Math.max(0, adjustedExpenses - guaranteedIncome - (showFiaModeling ? fiaIncome : 0));

        // Withdrawal is the lesser of: needed income, max withdrawal rate, or remaining balance
        const maxWithdrawal = (balance * (withdrawalRate / 100)) / 12;
        const withdrawal = Math.min(incomeGap, maxWithdrawal, balance / 12);

        // Portfolio grows/shrinks based on returns minus withdrawals
        const annualWithdrawal = withdrawal * 12;
        const growth = (balance - annualWithdrawal / 2) * scenarioReturn;
        const newBalance = Math.max(0, balance + growth - annualWithdrawal);

        return { income: withdrawal, newBalance };
      };

      // Calculate for each scenario
      const avgResult = calculatePortfolioIncome(portfolioBalanceAvg, SCENARIO_RETURNS.average);
      const pessResult = calculatePortfolioIncome(portfolioBalancePessimistic, SCENARIO_RETURNS.pessimistic);
      const optResult = calculatePortfolioIncome(portfolioBalanceOptimistic, SCENARIO_RETURNS.optimistic);

      portfolioBalanceAvg = avgResult.newBalance;
      portfolioBalancePessimistic = pessResult.newBalance;
      portfolioBalanceOptimistic = optResult.newBalance;

      // Get portfolio income based on active scenario
      const portfolioIncome = activeScenario === 'pessimistic'
        ? pessResult.income
        : activeScenario === 'optimistic'
          ? optResult.income
          : avgResult.income;

      const portfolioBalance = activeScenario === 'pessimistic'
        ? portfolioBalancePessimistic
        : activeScenario === 'optimistic'
          ? portfolioBalanceOptimistic
          : portfolioBalanceAvg;

      data.push({
        age,
        socialSecurity,
        pension,
        portfolioIncome,
        annuity: 0, // Placeholder for other annuities from income sources
        totalIncome: socialSecurity + pension + portfolioIncome,
        expenses: adjustedExpenses,
        portfolioBalance,
        // Multi-scenario data
        pessimisticTotal: socialSecurity + pension + pessResult.income,
        optimisticTotal: socialSecurity + pension + optResult.income,
        pessimisticBalance: portfolioBalancePessimistic,
        optimisticBalance: portfolioBalanceOptimistic,
        // FIA layer
        fiaIncome: fiaIncome,
        totalWithFia: socialSecurity + pension + portfolioIncome + fiaIncome,
      });
    }

    return data;
  }, [
    currentAge,
    retirementAge,
    lifeExpectancy,
    portfolioValue,
    monthlyExpenses,
    inflationRate,
    socialSecurityAmount,
    socialSecurityStartAge,
    pensionAmount,
    pensionStartAge,
    hasPension,
    withdrawalRate,
    activeScenario,
    showFiaModeling,
    fiaLumpSum,
    fiaPayoutRate,
    fiaStartAge,
  ]);

  // Calculate key metrics
  const metrics = useMemo(() => {
    const retirementData = projectionData.filter(d => d.age >= retirementAge);
    if (retirementData.length === 0) return null;

    // Years where income covers expenses
    const coveredYears = retirementData.filter(d => d.totalIncome >= d.expenses).length;
    const totalRetirementYears = retirementData.length;
    const coverageRate = (coveredYears / totalRetirementYears) * 100;

    // Portfolio longevity (when it runs out)
    const depletionAge = retirementData.find(d => d.portfolioBalance <= 0)?.age;

    // Average coverage ratio
    const avgCoverage = retirementData.reduce((sum, d) =>
      sum + (d.totalIncome / d.expenses), 0) / retirementData.length * 100;

    // Income gap at start of retirement
    const initialGap = retirementData[0]
      ? retirementData[0].expenses - retirementData[0].totalIncome
      : 0;

    // With FIA metrics
    const coveredYearsWithFia = showFiaModeling
      ? retirementData.filter(d => (d.totalWithFia || 0) >= d.expenses).length
      : coveredYears;

    return {
      coverageRate,
      coveredYears,
      totalRetirementYears,
      depletionAge,
      avgCoverage,
      initialGap,
      portfolioAtRetirement: projectionData.find(d => d.age === retirementAge)?.portfolioBalance || portfolioValue,
      coveredYearsWithFia,
      fiaImpact: coveredYearsWithFia - coveredYears,
    };
  }, [projectionData, retirementAge, portfolioValue, showFiaModeling]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatMonthly = (value: number) => `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Retirement Income Projection
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Ages {currentAge} - {lifeExpectancy}
            </Badge>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Visualize your income sources and portfolio drawdown through retirement
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Key Metrics Summary */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Coverage Rate</div>
              <div className={cn(
                "text-xl font-bold font-mono",
                metrics.coverageRate >= 90 ? "text-green-500" :
                metrics.coverageRate >= 70 ? "text-amber-500" : "text-red-500"
              )}>
                {metrics.coverageRate.toFixed(0)}%
              </div>
              <div className="text-[10px] text-muted-foreground">
                {metrics.coveredYears} of {metrics.totalRetirementYears} years
              </div>
            </div>

            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Portfolio at 65</div>
              <div className="text-xl font-bold font-mono text-primary">
                {formatCurrency(metrics.portfolioAtRetirement)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {withdrawalRate}% withdrawal rate
              </div>
            </div>

            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {metrics.depletionAge ? 'Portfolio Depletes' : 'Portfolio Lasts'}
              </div>
              <div className={cn(
                "text-xl font-bold font-mono",
                metrics.depletionAge ? "text-red-500" : "text-green-500"
              )}>
                {metrics.depletionAge ? `Age ${metrics.depletionAge}` : `To ${lifeExpectancy}+`}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {metrics.depletionAge
                  ? `${metrics.depletionAge - retirementAge} years of withdrawals`
                  : 'Sustainable income'}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Initial Gap</div>
              <div className={cn(
                "text-xl font-bold font-mono",
                metrics.initialGap <= 0 ? "text-green-500" : "text-amber-500"
              )}>
                {metrics.initialGap <= 0
                  ? 'None'
                  : formatMonthly(metrics.initialGap) + '/mo'}
              </div>
              <div className="text-[10px] text-muted-foreground">
                At retirement start
              </div>
            </div>
          </div>
        )}

        {/* Main Chart */}
        <Tabs defaultValue="income">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="income" className="text-xs">Income Streams</TabsTrigger>
            <TabsTrigger value="balance" className="text-xs">Portfolio Balance</TabsTrigger>
            <TabsTrigger value="settings" className="text-xs">Settings</TabsTrigger>
          </TabsList>

          {/* Income Streams Tab */}
          <TabsContent value="income" className="space-y-4 mt-4">
            {/* Scenario selector */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Market Scenario:</Label>
                <Select value={activeScenario} onValueChange={(v) => setActiveScenario(v as MarketScenario)}>
                  <SelectTrigger className="w-[180px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pessimistic" className="text-xs">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        Bear Market (4%)
                      </span>
                    </SelectItem>
                    <SelectItem value="average" className="text-xs">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        Average Market (7%)
                      </span>
                    </SelectItem>
                    <SelectItem value="optimistic" className="text-xs">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        Bull Market (10%)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-scenarios"
                    checked={showAllScenarios}
                    onCheckedChange={setShowAllScenarios}
                  />
                  <Label htmlFor="show-scenarios" className="text-xs">Show all scenarios</Label>
                </div>
              </div>
            </div>

            {/* Stacked Area Chart */}
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={projectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="socialSecurityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.3}/>
                    </linearGradient>
                    <linearGradient id="pensionGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    </linearGradient>
                    <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    </linearGradient>
                    <linearGradient id="fiaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="age"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(age) => `${age}`}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(val) => formatMonthly(val)}
                    width={60}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0]?.payload as ProjectionDataPoint;
                      return (
                        <div className="bg-background/95 border border-border rounded-lg p-3 shadow-lg text-xs">
                          <div className="font-semibold mb-2">Age {label}</div>
                          <div className="space-y-1">
                            <div className="flex justify-between gap-4">
                              <span className="text-green-500">Social Security:</span>
                              <span className="font-mono">{formatMonthly(data.socialSecurity)}</span>
                            </div>
                            {hasPension && (
                              <div className="flex justify-between gap-4">
                                <span className="text-blue-500">Pension:</span>
                                <span className="font-mono">{formatMonthly(data.pension)}</span>
                              </div>
                            )}
                            <div className="flex justify-between gap-4">
                              <span className="text-purple-500">Portfolio:</span>
                              <span className="font-mono">{formatMonthly(data.portfolioIncome)}</span>
                            </div>
                            {showFiaModeling && (data.fiaIncome ?? 0) > 0 && (
                              <div className="flex justify-between gap-4">
                                <span className="text-amber-500">FIA Income:</span>
                                <span className="font-mono">{formatMonthly(data.fiaIncome ?? 0)}</span>
                              </div>
                            )}
                            <div className="border-t border-border pt-1 mt-1">
                              <div className="flex justify-between gap-4 font-semibold">
                                <span>Total Income:</span>
                                <span className="font-mono">
                                  {formatMonthly(showFiaModeling && data.totalWithFia
                                    ? data.totalWithFia
                                    : data.totalIncome)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-4 text-muted-foreground">
                                <span>Expenses:</span>
                                <span className="font-mono">{formatMonthly(data.expenses)}</span>
                              </div>
                              <div className={cn(
                                "flex justify-between gap-4 font-semibold",
                                (showFiaModeling && data.totalWithFia ? data.totalWithFia : data.totalIncome) >= data.expenses
                                  ? "text-green-500"
                                  : "text-red-500"
                              )}>
                                <span>
                                  {(showFiaModeling && data.totalWithFia ? data.totalWithFia : data.totalIncome) >= data.expenses
                                    ? 'Surplus:'
                                    : 'Shortfall:'}
                                </span>
                                <span className="font-mono">
                                  {formatMonthly(Math.abs(
                                    (showFiaModeling && data.totalWithFia ? data.totalWithFia : data.totalIncome) - data.expenses
                                  ))}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />

                  {/* Reference lines */}
                  <ReferenceLine
                    x={retirementAge}
                    stroke="#888"
                    strokeDasharray="3 3"
                    label={{ value: 'Retire', fontSize: 10, fill: '#888' }}
                  />

                  {/* Stacked areas for income sources */}
                  <Area
                    type="monotone"
                    dataKey="socialSecurity"
                    stackId="1"
                    stroke="#22c55e"
                    fill="url(#socialSecurityGradient)"
                    name="Social Security"
                  />
                  {hasPension && (
                    <Area
                      type="monotone"
                      dataKey="pension"
                      stackId="1"
                      stroke="#3b82f6"
                      fill="url(#pensionGradient)"
                      name="Pension"
                    />
                  )}
                  <Area
                    type="monotone"
                    dataKey="portfolioIncome"
                    stackId="1"
                    stroke="#8b5cf6"
                    fill="url(#portfolioGradient)"
                    name="Portfolio Withdrawals"
                  />
                  {showFiaModeling && (
                    <Area
                      type="monotone"
                      dataKey="fiaIncome"
                      stackId="1"
                      stroke="#f59e0b"
                      fill="url(#fiaGradient)"
                      name="Fixed Index Annuity"
                    />
                  )}

                  {/* Expense line */}
                  <Line
                    type="monotone"
                    dataKey="expenses"
                    stroke="#ef4444"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    name="Monthly Expenses"
                  />

                  <Legend
                    wrapperStyle={{ fontSize: '10px' }}
                    iconType="rect"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* FIA Modeling Panel */}
            <Collapsible open={showFiaModeling} onOpenChange={setShowFiaModeling}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between h-10">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium">Model Fixed Index Annuity</span>
                  </div>
                  {showFiaModeling ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-4">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-muted-foreground">
                      A Fixed Index Annuity (FIA) converts a lump sum into guaranteed lifetime income.
                      Model how adding an FIA could impact your income security.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Premium (Lump Sum)</Label>
                      <Input
                        type="number"
                        value={fiaLumpSum}
                        onChange={(e) => setFiaLumpSum(Number(e.target.value))}
                        className="h-9 font-mono text-sm"
                        step={10000}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Payout Rate (%)</Label>
                      <Input
                        type="number"
                        value={fiaPayoutRate}
                        onChange={(e) => setFiaPayoutRate(Number(e.target.value))}
                        className="h-9 font-mono text-sm"
                        step={0.1}
                        min={3}
                        max={8}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Start Age</Label>
                      <Input
                        type="number"
                        value={fiaStartAge}
                        onChange={(e) => setFiaStartAge(Number(e.target.value))}
                        className="h-9 font-mono text-sm"
                      />
                    </div>
                  </div>

                  {/* FIA Impact Summary */}
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <div className="p-2 rounded bg-background/50 text-center">
                      <div className="text-[10px] text-muted-foreground uppercase">Monthly Income</div>
                      <div className="font-mono font-semibold text-amber-500">
                        {formatMonthly((fiaLumpSum * (fiaPayoutRate / 100)) / 12)}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-background/50 text-center">
                      <div className="text-[10px] text-muted-foreground uppercase">Annual Income</div>
                      <div className="font-mono font-semibold text-amber-500">
                        {formatCurrency(fiaLumpSum * (fiaPayoutRate / 100))}
                      </div>
                    </div>
                    <div className="p-2 rounded bg-background/50 text-center">
                      <div className="text-[10px] text-muted-foreground uppercase">Coverage Impact</div>
                      <div className={cn(
                        "font-mono font-semibold",
                        metrics && metrics.fiaImpact > 0 ? "text-green-500" : "text-muted-foreground"
                      )}>
                        {metrics ? `+${metrics.fiaImpact} years` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </TabsContent>

          {/* Portfolio Balance Tab */}
          <TabsContent value="balance" className="space-y-4 mt-4">
            <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="pessimisticGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="optimisticGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>

                  <XAxis
                    dataKey="age"
                    tick={{ fontSize: 10 }}
                    interval={4}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(val) => formatCurrency(val)}
                    width={60}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const data = payload[0]?.payload as ProjectionDataPoint;
                      return (
                        <div className="bg-background/95 border border-border rounded-lg p-3 shadow-lg text-xs">
                          <div className="font-semibold mb-2">Age {label}</div>
                          <div className="space-y-1">
                            {showAllScenarios && (
                              <>
                                <div className="flex justify-between gap-4">
                                  <span className="text-green-500">Optimistic:</span>
                                  <span className="font-mono">{formatCurrency(data.optimisticBalance || 0)}</span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between gap-4">
                              <span className="text-purple-500">Portfolio Balance:</span>
                              <span className="font-mono">{formatCurrency(data.portfolioBalance)}</span>
                            </div>
                            {showAllScenarios && (
                              <div className="flex justify-between gap-4">
                                <span className="text-red-500">Pessimistic:</span>
                                <span className="font-mono">{formatCurrency(data.pessimisticBalance || 0)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />

                  <ReferenceLine
                    x={retirementAge}
                    stroke="#888"
                    strokeDasharray="3 3"
                    label={{ value: 'Retire', fontSize: 10, fill: '#888' }}
                  />

                  {/* Show scenario bands if enabled */}
                  {showAllScenarios && (
                    <>
                      <Area
                        type="monotone"
                        dataKey="optimisticBalance"
                        stroke="#22c55e"
                        fill="url(#optimisticGradient)"
                        strokeWidth={1}
                        name="Optimistic"
                      />
                      <Area
                        type="monotone"
                        dataKey="pessimisticBalance"
                        stroke="#ef4444"
                        fill="url(#pessimisticGradient)"
                        strokeWidth={1}
                        name="Pessimistic"
                      />
                    </>
                  )}

                  <Area
                    type="monotone"
                    dataKey="portfolioBalance"
                    stroke="#8b5cf6"
                    fill="url(#balanceGradient)"
                    strokeWidth={2}
                    name="Portfolio Balance"
                  />

                  <Legend
                    wrapperStyle={{ fontSize: '10px' }}
                    iconType="rect"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-all-scenarios"
                  checked={showAllScenarios}
                  onCheckedChange={setShowAllScenarios}
                />
                <Label htmlFor="show-all-scenarios" className="text-xs">
                  Show all market scenarios
                </Label>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Age Settings */}
              <div className="space-y-4 p-4 rounded-lg bg-muted/30 border border-border/50">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Timeline
                </h4>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Current Age</Label>
                      <span className="text-xs font-mono">{currentAge}</span>
                    </div>
                    <Slider
                      value={[currentAge]}
                      onValueChange={([v]) => setCurrentAge(v)}
                      min={30}
                      max={80}
                      step={1}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Retirement Age</Label>
                      <span className="text-xs font-mono">{retirementAge}</span>
                    </div>
                    <Slider
                      value={[retirementAge]}
                      onValueChange={([v]) => setRetirementAge(v)}
                      min={55}
                      max={75}
                      step={1}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Life Expectancy</Label>
                      <span className="text-xs font-mono">{lifeExpectancy}</span>
                    </div>
                    <Slider
                      value={[lifeExpectancy]}
                      onValueChange={([v]) => setLifeExpectancy(v)}
                      min={80}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>
              </div>

              {/* Financial Settings */}
              <div className="space-y-4 p-4 rounded-lg bg-muted/30 border border-border/50">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Financials
                </h4>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Portfolio Value</Label>
                    <Input
                      type="number"
                      value={portfolioValue}
                      onChange={(e) => setPortfolioValue(Number(e.target.value))}
                      className="h-9 font-mono text-sm"
                      step={10000}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Monthly Expenses</Label>
                    <Input
                      type="number"
                      value={monthlyExpenses}
                      onChange={(e) => setMonthlyExpenses(Number(e.target.value))}
                      className="h-9 font-mono text-sm"
                      step={100}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <Label className="text-xs">Withdrawal Rate</Label>
                      <span className="text-xs font-mono">{withdrawalRate}%</span>
                    </div>
                    <Slider
                      value={[withdrawalRate]}
                      onValueChange={([v]) => setWithdrawalRate(v)}
                      min={2}
                      max={6}
                      step={0.25}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Income Sources */}
            <div className="space-y-4 p-4 rounded-lg bg-muted/30 border border-border/50">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Guaranteed Income Sources
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {/* Social Security */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Social Security</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Monthly</Label>
                      <Input
                        type="number"
                        value={socialSecurityAmount}
                        onChange={(e) => setSocialSecurityAmount(Number(e.target.value))}
                        className="h-8 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Start Age</Label>
                      <Input
                        type="number"
                        value={socialSecurityStartAge}
                        onChange={(e) => setSocialSecurityStartAge(Number(e.target.value))}
                        className="h-8 font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Pension */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium">Pension</Label>
                    <Switch
                      checked={hasPension}
                      onCheckedChange={setHasPension}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Monthly</Label>
                      <Input
                        type="number"
                        value={pensionAmount}
                        onChange={(e) => setPensionAmount(Number(e.target.value))}
                        className="h-8 font-mono text-xs"
                        disabled={!hasPension}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground">Start Age</Label>
                      <Input
                        type="number"
                        value={pensionStartAge}
                        onChange={(e) => setPensionStartAge(Number(e.target.value))}
                        className="h-8 font-mono text-xs"
                        disabled={!hasPension}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Inflation */}
            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Inflation Rate</Label>
                  <span className="text-xs font-mono text-muted-foreground">{inflationRate.toFixed(1)}%</span>
                </div>
                <Slider
                  value={[inflationRate]}
                  onValueChange={([v]) => setInflationRate(v)}
                  min={1}
                  max={5}
                  step={0.25}
                  className="w-32"
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
